import React, { useState, useEffect } from 'react';
import { getDaysUntil, getTodoStatus } from '../utils/helpers';
import { fetchCalendarEvents } from '../utils/storage';

export default function Home({ holidays, members, shoppingList, todos = [], user, navigate, onManageMembers }) {
  const firstName = user?.name?.split(' ')[0] || 'there';

  // Todos assigned to the current user that need attention (in-app reminder)
  const myMember = members.find(m => m.email === user?.email);
  const openTodos = todos.filter(t => !t.done);
  const myDueTodos = openTodos.filter(t => {
    const status = getTodoStatus(t);
    if (status !== 'overdue' && status !== 'due-soon') return false;
    if (Array.isArray(t.assignedTo) && t.assignedTo.length > 0) {
      return myMember ? t.assignedTo.includes(myMember.id) : false;
    }
    return true;
  });

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState(null);

  useEffect(() => {
    fetchCalendarEvents()
      .then(events => {
        setCalendarEvents(events);
        setCalendarLoading(false);
      })
      .catch(err => {
        setCalendarError(err.message);
        setCalendarLoading(false);
      });
  }, []);

  // Filter events to next 7 days
  const sevenDaysOut = new Date();
  sevenDaysOut.setDate(sevenDaysOut.getDate() + 7);
  const weekEvents = calendarEvents.filter(e => new Date(e.start) <= sevenDaysOut);

  // Group events by day
  const groupedEvents = weekEvents.reduce((groups, event) => {
    const date = new Date(event.start);
    const key = date.toISOString().split('T')[0];
    if (!groups[key]) groups[key] = [];
    groups[key].push(event);
    return groups;
  }, {});

  const formatDayLabel = (dateKey) => {
    const date = new Date(dateKey + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.getTime() === today.getTime()) return 'Today';
    if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
  };

  const formatEventTime = (dateStr) => {
    if (dateStr.length === 10) return 'All day';
    return new Date(dateStr).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });
  };

  const booked = holidays.filter(h => h.status === 'booked' || h.status === 'planning');
  const nextTrip = booked
    .filter(h => getDaysUntil(h.departureDate) >= 0)
    .sort((a, b) => new Date(a.departureDate) - new Date(b.departureDate))[0];

  return (
    <div className="animate-fade-in-up">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Hey {firstName} 👋</h2>
        <p className="text-gray-500 mt-1">What are we up to today?</p>
      </div>

      {/* In-app todo reminder */}
      {myDueTodos.length > 0 && (
        <button
          onClick={() => navigate('todos')}
          className="w-full mb-4 text-left bg-red-50 border border-red-200 rounded-2xl p-4 hover:bg-red-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">⏰</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-red-700">
                {myDueTodos.length} task{myDueTodos.length !== 1 ? 's' : ''} due
              </p>
              <p className="text-xs text-red-500 truncate">
                {myDueTodos.map(t => t.text).join(', ')}
              </p>
            </div>
            <span className="text-xs text-red-400">View →</span>
          </div>
        </button>
      )}

      {/* Calendar — next 7 days */}
      <button
        onClick={() => navigate('calendar')}
        className="w-full bg-white rounded-2xl p-5 shadow-sm border border-coral-200 hover:shadow-md hover:border-coral-300 transition-all text-left mb-4"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            📅 This week
          </h3>
          <span className="text-xs text-gray-400">View all →</span>
        </div>

        {calendarLoading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-coral-400" />
          </div>
        ) : calendarError ? (
          <p className="text-sm text-gray-400 py-2">Calendar unavailable</p>
        ) : weekEvents.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">Nothing planned this week</p>
        ) : (
          <div className="space-y-3">
            {Object.keys(groupedEvents).sort().map(dateKey => (
              <div key={dateKey}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                  {formatDayLabel(dateKey)}
                </p>
                <div className="space-y-1.5">
                  {groupedEvents[dateKey].map(event => (
                    <div key={event.id} className="flex items-start gap-3">
                      <span className="text-xs text-coral-500 font-medium w-14 pt-0.5 shrink-0">
                        {formatEventTime(event.start)}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{event.title}</p>
                        {event.location && (
                          <p className="text-xs text-gray-400 truncate">{event.location}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </button>

      {/* Quick links row */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => navigate('holidays')}
          className="bg-white rounded-2xl p-4 shadow-sm border border-purple-100 hover:shadow-md hover:border-purple-200 transition-all text-left"
        >
          <div className="text-2xl mb-2">✈️</div>
          <h3 className="text-sm font-bold text-gray-800">Holidays</h3>
          {nextTrip ? (
            <p className="text-xs text-purple-600 mt-1 truncate">
              {nextTrip.emoji} {getDaysUntil(nextTrip.departureDate)}d
            </p>
          ) : (
            <p className="text-xs text-gray-400 mt-1">
              {booked.length} trip{booked.length !== 1 ? 's' : ''}
            </p>
          )}
        </button>

        <button
          onClick={() => navigate('shopping')}
          className="bg-white rounded-2xl p-4 shadow-sm border border-teal-100 hover:shadow-md hover:border-teal-200 transition-all text-left"
        >
          <div className="text-2xl mb-2">🛒</div>
          <h3 className="text-sm font-bold text-gray-800">Shopping</h3>
          <p className="text-xs text-gray-400 mt-1">
            {shoppingList.length === 0 ? 'Empty' : `${shoppingList.length} item${shoppingList.length !== 1 ? 's' : ''}`}
          </p>
        </button>

        <button
          onClick={() => navigate('todos')}
          className="bg-white rounded-2xl p-4 shadow-sm border border-purple-100 hover:shadow-md hover:border-purple-200 transition-all text-left"
        >
          <div className="text-2xl mb-2">✅</div>
          <h3 className="text-sm font-bold text-gray-800">To-Do</h3>
          <p className="text-xs text-gray-400 mt-1">
            {openTodos.length === 0 ? 'All clear' : `${openTodos.length} task${openTodos.length !== 1 ? 's' : ''}`}
          </p>
        </button>

        <button
          onClick={onManageMembers}
          className="bg-white rounded-2xl p-4 shadow-sm border border-sunny-200 hover:shadow-md hover:border-sunny-300 transition-all text-left"
        >
          <div className="text-2xl mb-2">👨‍👩‍👧‍👦</div>
          <h3 className="text-sm font-bold text-gray-800">Family</h3>
          <p className="text-xs text-gray-400 mt-1">
            {members.length} member{members.length !== 1 ? 's' : ''}
          </p>
        </button>
      </div>
    </div>
  );
}
